/**
 * Floating controls while trainer screen-shares.
 *
 * Entire-screen capture includes every painted pixel. We hide LMS chrome
 * during share and only briefly show this strip (auto-hides) so students
 * mostly see stage content — not Pause / Manage topic / sidebars.
 *
 * Flow:
 *   step 1) Parent hides chrome when isScreenShareEnabled
 *   step 2) This strip appears on pointer move near bottom; auto-hides
 *   step 3) Stop Share restores full trainer UI
 */

import React, { useEffect, useState } from "react";
import { LayoutDashboard, Mic, MicOff, MonitorOff } from "lucide-react";

type ScreenSharePrivacyControlsProps = {
  isMicrophoneEnabled: boolean;
  onToggleMicrophone: () => void;
  onStopShare: () => void;
  /** Temporarily show top bar / Meeting Tools (warn: visible in Entire Screen). */
  onShowTools?: () => void;
  /** True when Entire Screen is active — trainer LMS view is blanked. */
  entireScreen?: boolean;
};

export function ScreenSharePrivacyControls({
  isMicrophoneEnabled,
  onToggleMicrophone,
  onStopShare,
  onShowTools,
  entireScreen = false,
}: ScreenSharePrivacyControlsProps) {
  // ---------------------------------------------------------------
  // step 1: Auto-hide strip so it is rarely in the captured frame
  // ---------------------------------------------------------------
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    let hideTimer = window.setTimeout(() => setVisible(false), 2800);

    const onMove = (e: MouseEvent) => {
      const nearBottom = e.clientY > window.innerHeight - 96;
      if (!nearBottom) return;
      setVisible(true);
      window.clearTimeout(hideTimer);
      hideTimer = window.setTimeout(() => setVisible(false), 2800);
    };

    window.addEventListener("mousemove", onMove);
    return () => {
      window.clearTimeout(hideTimer);
      window.removeEventListener("mousemove", onMove);
    };
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        left: "50%",
        bottom: 20,
        transform: "translateX(-50%)",
        zIndex: 240,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? "auto" : "none",
        transition: "opacity 0.25s ease",
      }}
    >
      <p
        style={{
          margin: 0,
          padding: "4px 10px",
          borderRadius: 999,
          background: "rgba(15,23,42,0.75)",
          color: "#cbd5e1",
          fontSize: 11,
          fontWeight: 600,
        }}
      >
        Sharing — top bar & Meeting Tools hidden
        {entireScreen ? " (entire screen)" : ""}. Move mouse here for controls.
      </p>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 10px",
          borderRadius: 14,
          background: "rgba(15, 23, 42, 0.92)",
          border: "1px solid rgba(255,255,255,0.12)",
          boxShadow: "0 12px 40px rgba(0,0,0,0.45)",
        }}
      >
        <button
          type="button"
          onClick={onToggleMicrophone}
          title={isMicrophoneEnabled ? "Mute" : "Unmute"}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            border: "none",
            borderRadius: 10,
            padding: "8px 12px",
            background: isMicrophoneEnabled
              ? "rgba(255,255,255,0.08)"
              : "rgba(220,38,38,0.25)",
            color: isMicrophoneEnabled ? "#e2e8f0" : "#fca5a5",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {isMicrophoneEnabled ? <Mic size={16} /> : <MicOff size={16} />}
          {isMicrophoneEnabled ? "Mute" : "Unmute"}
        </button>
        {onShowTools ? (
          <button
            type="button"
            onClick={onShowTools}
            title="Show Meeting Tools (students may see them if sharing Entire Screen)"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              border: "none",
              borderRadius: 10,
              padding: "8px 12px",
              background: "rgba(255,255,255,0.08)",
              color: "#e2e8f0",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <LayoutDashboard size={16} />
            Show tools
          </button>
        ) : null}
        <button
          type="button"
          onClick={onStopShare}
          title="Stop Share"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            border: "none",
            borderRadius: 10,
            padding: "8px 14px",
            background: "#2563eb",
            color: "#fff",
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          <MonitorOff size={16} />
          Stop Share
        </button>
      </div>
    </div>
  );
}
